/**
 * Felles fasit for team-wide BF-tall (én kilde, så widgets ikke kan divergere).
 *
 * Brukes av BF-over-tid, Bonusmål-BF og evt. flere. Definisjonen er låst i
 * docs/kpi-source-of-truth.md (§bf.year_totals).
 *
 * - Reell  = Σ mål-GEEK for AVSLUTTEDE prosjekter (completed) med completed_at
 *   i året. 2026 trekker ut godkjent arbeid fra før (geekForMaal).
 * - Prognose = Σ bf_estimated for PÅGÅENDE prosjekter (active/kommende) med
 *   estimated_deadline i året — INKLUDERT forfalt dato (Erik 2026-06: forfalte
 *   aktive prosjekter lander trolig fortsatt i år, så de teller).
 * - Udaterte (pågående uten estimated_deadline) kan ikke periodiseres og
 *   ekskluderes fra prognosen — telles separat (undatedCount).
 * - Kansellerte og avsluttede-uten-completed_at-i-året ekskluderes.
 * - Interne prosjekter (is_internal) har ingen bruttofortjeneste og ekskluderes
 *   helt — fra reell, prognose og undatedCount.
 */

// VIB-1452 terminologi: BF i kode/DB = GEEK i UI, hjelp, rapporter og SOT.
import { geekForMaal } from "./geek-maal-adjustment";

export interface BfBucketProject {
  project_status: string | null;
  bf_actual: number | null;
  bf_estimated: number | null;
  completed_at: string | null;
  estimated_deadline: string | null;
  is_internal: boolean | null;
  project_code?: string | null;
}

export interface BfYearTotals {
  reellYtd: number;
  prognose: number;
  forventet: number;
  undatedCount: number;
}

function yearOf(iso: string): number {
  return Number(iso.slice(0, 4));
}

export const isBfCompleted = (p: BfBucketProject) => p.project_status === 'completed';
export const isBfPipeline = (p: BfBucketProject) =>
  p.project_status === 'active' || p.project_status === 'kommende';

export interface BfYearContribution<P extends BfBucketProject> {
  project: P;
  kind: 'reell' | 'prognose';
  /** Reell: mål-GEEK (geekForMaal). Prognose: bf_estimated, inkl. forfalt. */
  amount: number;
}

/**
 * Prosjektene bak `calcBfYearTotals`, ett bidrag per prosjekt. Totalene
 * summeres herfra, så drilldown og tall kan ikke skille lag (VIB-2990).
 */
export function calcBfYearContributions<P extends BfBucketProject>(
  projects: P[],
  year: number,
): { contributions: BfYearContribution<P>[]; undatedCount: number } {
  const contributions: BfYearContribution<P>[] = [];
  let undatedCount = 0;

  for (const p of projects) {
    if (p.project_status === 'cancelled') continue;
    if (p.is_internal) continue; // interne har ingen BF

    if (isBfCompleted(p)) {
      if (p.completed_at && yearOf(p.completed_at) === year) {
        contributions.push({ project: p, kind: 'reell', amount: geekForMaal(p, year) });
      }
    } else if (isBfPipeline(p)) {
      const ed = p.estimated_deadline;
      if (ed && yearOf(ed) === year) {
        contributions.push({ project: p, kind: 'prognose', amount: Number(p.bf_estimated ?? 0) }); // inkl. forfalt
      } else if (!ed) {
        undatedCount += 1;
      }
      // est_deadline i annet år → ignoreres
    }
  }

  return { contributions, undatedCount };
}

export function calcBfYearTotals(projects: BfBucketProject[], year: number): BfYearTotals {
  const { contributions, undatedCount } = calcBfYearContributions(projects, year);
  let reellYtd = 0;
  let prognose = 0;
  for (const c of contributions) {
    if (c.kind === 'reell') reellYtd += c.amount;
    else prognose += c.amount;
  }
  return { reellYtd, prognose, forventet: reellYtd + prognose, undatedCount };
}
